import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Upload, Camera, X, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { BACKEND_URL } from '@/config/api';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, token, updateUser, refreshUser } = useAuth();
  const fileInputRef = useRef(null);
  
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [profileData, setProfileData] = useState({
    fullName: '',
    username: '',
    displayName: '',
    title: '',
    timezone: 'UTC',
    avatarUrl: null
  });

  useEffect(() => {
    if (user) {
      setProfileData(prev => ({
        ...prev,
        fullName: user.name || '',
        username: user.username || user.email?.split('@')[0] || '',
        displayName: user.display_name || user.name?.split(' ')[0] || '',
        title: user.title || '',
        timezone: user.timezone || 'UTC',
        avatarUrl: user.avatar_url || null
      }));
    }
  }, [user]);

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
    setSaveSuccess(false);
  };

  const handleSaveChanges = async () => {
    setIsLoading(true);
    setSaveSuccess(false);
    try {
      const response = await fetch(`${BACKEND_URL}/api/users/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: profileData.fullName,
          display_name: profileData.displayName,
          username: profileData.username,
          title: profileData.title,
          timezone: profileData.timezone
        })
      });
      
      if (response.ok) {
        const updatedUser = await response.json();
        updateUser(updatedUser);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        alert('Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('An error occurred while updating profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size
    if (file.size > 2 * 1024 * 1024) {
      alert('Image must be less than 2MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('File must be an image');
      return;
    }

    setIsUploadingPhoto(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${BACKEND_URL}/api/users/profile/avatar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setProfileData(prev => ({
          ...prev,
          avatarUrl: data.avatar_url
        }));
        updateUser({ avatar_url: data.avatar_url });
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to upload photo');
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('An error occurred while uploading photo');
    } finally {
      setIsUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemovePhoto = async () => {
    setIsUploadingPhoto(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/users/profile/avatar`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setProfileData(prev => ({
          ...prev,
          avatarUrl: null
        }));
        updateUser({ avatar_url: null });
      }
    } catch (error) {
      console.error('Error removing photo:', error);
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const timezones = [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Asia/Singapore',
    'Australia/Sydney'
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 overflow-x-hidden" data-testid="profile-page">
      {/* Back Button */}
      <div className="max-w-2xl mx-auto pt-4 sm:pt-8 px-4 sm:px-6">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors mb-8"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft size={20} />
          <span>Back to Dashboard</span>
        </button>

        {/* Profile Card */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 lg:p-8">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-1">Profile</h1>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Manage your personal information and preferences</p>

          {/* Photo Upload */}
          <div className="flex items-center gap-6 mb-8">
            <div className="relative group">
              <div 
                className="w-24 h-24 rounded-full flex items-center justify-center text-white text-3xl font-medium overflow-hidden ring-4 ring-gray-100 dark:ring-gray-700"
                style={{ backgroundColor: user?.avatar_color || '#6366F1' }}
              >
                {profileData.avatarUrl ? (
                  <img 
                    src={profileData.avatarUrl} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                    data-testid="profile-avatar"
                  />
                ) : (
                  <span data-testid="profile-initial">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </span>
                )}
              </div>
              
              {/* Hover overlay */}
              <div 
                className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <Camera size={24} className="text-white" />
              </div>
              
              {/* Loading spinner */}
              {isUploadingPhoto && (
                <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
            
            <div>
              <div className="flex items-center gap-2">
                <label className="cursor-pointer">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                    onChange={handlePhotoUpload}
                    data-testid="photo-upload-input"
                  />
                  <div className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <Upload size={16} className="text-gray-500 dark:text-gray-400" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {profileData.avatarUrl ? 'Change Photo' : 'Upload Photo'}
                    </span>
                  </div>
                </label>
                
                {profileData.avatarUrl && (
                  <button
                    onClick={handleRemovePhoto}
                    className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    title="Remove photo"
                    data-testid="remove-photo-btn"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">JPG, PNG, GIF or WebP. Max size 2MB.</p>
            </div>
          </div>

          {/* Form Fields */}
          <div className="space-y-6">
            {/* Email */}
            <div className="space-y-2">
              <Label className="text-gray-700 dark:text-gray-300">Email</Label>
              <Input
                value={user?.email || ''}
                disabled
                className="bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-600"
                data-testid="email-input"
              />
              <p className="text-xs text-gray-400 dark:text-gray-500">Email cannot be changed</p>
            </div>

            {/* Full Name */}
            <div className="space-y-2">
              <Label className="text-gray-700 dark:text-gray-300">Full Name</Label>
              <Input
                value={profileData.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                placeholder="Your full name"
                className="border-gray-200 dark:border-gray-600 dark:bg-slate-700 dark:text-white focus:border-blue-500"
                data-testid="fullname-input"
              />
            </div>

            {/* Username */}
            <div className="space-y-2">
              <Label className="text-gray-700 dark:text-gray-300">Username</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">@</span>
                <Input
                  value={profileData.username}
                  onChange={(e) => handleInputChange('username', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  placeholder="your_username"
                  className="pl-8 border-gray-200 dark:border-gray-600 dark:bg-slate-700 dark:text-white focus:border-blue-500"
                  data-testid="username-input"
                />
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500">Used for @mentions. Letters, numbers, and underscores only.</p>
            </div>

            {/* Display Name */}
            <div className="space-y-2">
              <Label className="text-gray-700 dark:text-gray-300">Display Name</Label>
              <Input
                value={profileData.displayName}
                onChange={(e) => handleInputChange('displayName', e.target.value)}
                placeholder="How you want to be called"
                className="border-gray-200 dark:border-gray-600 dark:bg-slate-700 dark:text-white focus:border-blue-500"
                data-testid="displayname-input"
              />
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label className="text-gray-700 dark:text-gray-300">Title</Label>
              <Input
                value={profileData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="e.g., Product Manager"
                className="border-gray-200 dark:border-gray-600 dark:bg-slate-700 dark:text-white focus:border-blue-500"
                data-testid="title-input"
              />
            </div>

            {/* Timezone */}
            <div className="space-y-2">
              <Label className="text-gray-700 dark:text-gray-300">Timezone</Label>
              <select
                value={profileData.timezone}
                onChange={(e) => handleInputChange('timezone', e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                data-testid="timezone-select"
              >
                {timezones.map(tz => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex items-center justify-end gap-3 mt-8">
            {saveSuccess && (
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                <Check size={18} />
                <span className="text-sm">Saved successfully!</span>
              </div>
            )}
            <Button
              onClick={handleSaveChanges}
              disabled={isLoading}
              className="bg-[#F97316] hover:bg-[#EA580C] text-white px-6"
              data-testid="save-changes-btn"
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
